/* eslint-disable camelcase */
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import styles from '@/pages/app/BillingOps/styles.module.scss'

import Input from '@/components/Input'
import { ConvertToSalesOwnedInput } from '@/pages/app/BillingOps/types'

const POSITIVE_INT = /^[1-9]\d*$/

const schema = z.object({
  seats: z.string().regex(POSITIVE_INT, 'Seats must be a whole number above 0'),
  seat_cap: z.union([
    z.literal(''),
    z.string().regex(POSITIVE_INT, 'Seat cap must be empty or a whole number above 0'),
  ]),
})

type FormValues = z.infer<typeof schema>

interface ConvertToSalesOwnedFormProps {
  disabled: boolean
  onSubmit: (input: ConvertToSalesOwnedInput) => void
  onChange: () => void
  formId: string
  banner?: string
}

const DEFAULT_BANNER =
  'Moves the General cost center to Salesforce billing. Nothing changes in Stripe: Support must cancel the ' +
  'subscription and delete any listed drafts by hand.'

const ConvertToSalesOwnedForm = ({
  disabled,
  onSubmit,
  onChange,
  formId,
  banner = DEFAULT_BANNER,
}: ConvertToSalesOwnedFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { seats: '', seat_cap: '' },
  })

  const submit = handleSubmit((values) =>
    onSubmit({ seats: Number(values.seats), seat_cap: values.seat_cap === '' ? null : Number(values.seat_cap) })
  )

  return (
    <form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      className={styles.section}
    >
      <div className={styles.warningBanner}>{banner}</div>

      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="convert-sales-owned-seats">
          Recorded seats
        </label>
        <Controller
          name="seats"
          control={control}
          render={({ field }) => (
            <Input
              id="convert-sales-owned-seats"
              type="text"
              inputMode="numeric"
              value={field.value}
              onInput={(text) => {
                field.onChange(text)
                onChange()
              }}
              placeholder="50"
              className={styles.input}
              isDisabled={disabled}
            />
          )}
        />
        <p className={styles.helperText}>Seats purchased in Salesforce. Informational only; never enforced.</p>
        {errors.seats && <p className={styles.errorText}>{errors.seats.message}</p>}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="convert-sales-owned-seat-cap">
          Seat cap (optional)
        </label>
        <Controller
          name="seat_cap"
          control={control}
          render={({ field }) => (
            <Input
              id="convert-sales-owned-seat-cap"
              type="text"
              inputMode="numeric"
              value={field.value}
              onInput={(text) => {
                field.onChange(text)
                onChange()
              }}
              placeholder="Empty lets the org grow"
              className={styles.input}
              isDisabled={disabled}
            />
          )}
        />
        <p className={styles.helperText}>Contractual limit on billable members and invites.</p>
        {errors.seat_cap && <p className={styles.errorText}>{errors.seat_cap.message}</p>}
      </div>
    </form>
  )
}

export default ConvertToSalesOwnedForm
